import { QuestionnaireData } from '@/types/category';
import { GroupSession } from '@/types/groupSession';
import { getPersonalizationName, NamePersonalizationResult } from './namePersonalizationUtils';
import { createEnvironmentContext } from './environmentParser';

// Helper function to create environment personalization section
export const createEnvironmentPersonalization = (environment?: string, useEnvironment?: boolean, categoryName?: string): string => {
  if (!environment || environment.trim().length === 0 || !useEnvironment) {
    return '';
  }

  const categorySpecificInstructions = {
    meditation: 'Acknowledge their actual physical position, what they can see, hear, and feel around them. Reference these real environmental details to ground them in the present moment.',
    anxiety: 'Reference their real surroundings, actual position, and immediate sensory environment to provide grounding in their current reality and reduce anxiety through present-moment anchoring.',
    sleep: 'Acknowledge their actual sleep setup, physical comfort elements, and real bedroom environment to enhance connection with their current resting space.',
    motivation: 'Reference their actual workspace, physical position, and immediate surroundings to connect their real environment with their goals and motivation.',
    memory: 'Use their actual physical location, real environmental details, and current position as anchors to enhance memory techniques and spatial recall.',
    massage: 'Acknowledge their actual physical position, real comfort setup, and current environmental elements to deepen body awareness and relaxation.',
    be_still: 'Reference their real current environment, actual physical position, and immediate surroundings to help them find stillness within their existing space.',
    self_love: 'Acknowledge their actual physical surroundings, current position, and immediate sensory environment to ground self-compassion work in their present reality.'
  };

  const specificInstructions = categorySpecificInstructions[categoryName as keyof typeof categorySpecificInstructions] || 
    'Use their current environment and physical setting to enhance the session experience and create natural, present-moment awareness.';

return `
${createEnvironmentContext(environment)}
ENVIRONMENTAL INTEGRATION WITH NATURAL PERSONALIZATION:
- ${specificInstructions}
- Address the user BY NAME when environmental context enhances the therapeutic moment
- Weave environmental essence naturally without forced repetition
- Use their physical setting to support therapeutic goals through organic acknowledgment
- Include 2-3 natural environmental references that enhance rather than dominate the session
- Combine name usage with environmental context when it adds therapeutic value
- Examples: "[Name], as you transition from your day...", "[Name], in this quiet moment...", "[Name], feeling grounded right where you are..."
- Focus on making them feel understood and present rather than repeatedly referencing their description`;
};

// Helper function to create biographical personalization section
const createBiographicalPersonalization = (userBiography?: string, categoryName?: string): string => {
  if (!userBiography || userBiography.trim().length === 0) {
    return '';
  }

  const categorySpecificInstructions = {
    meditation: 'Reference their living environment, relationships, and personal challenges to create grounding anchors during meditation.',
    anxiety: 'Use their support systems, coping mechanisms, and safe places to build calming strategies and reassurance.',
    sleep: 'Incorporate their bedroom environment, bedtime routines, and peaceful memories to enhance sleep preparation.',
    motivation: 'Draw from their goals, past achievements, personal values, and life aspirations to fuel inspiration and drive.',
    memory: 'Connect to their important relationships, meaningful experiences, and learning contexts to enhance recall techniques.',
    massage: 'Reference their physical environment, stress sources, and comfort preferences to personalize relaxation guidance.',
    be_still: 'Use their emotional support systems, coping strategies, and calming environments to guide emotional reset.',
    self_love: 'Incorporate their personal strengths, relationships, achievements, and values to build genuine self-appreciation.',
    zen_af: 'Use their specific biographical details for authentic therapeutic profanity that serves healing. Reference their actual circumstances, relationships, and challenges to create personalized empowering content. Generate original therapeutic language from their real situation - avoid generic template phrases in favor of authentic biographical integration that validates their unique experience through creative, healing-focused expression.'
  };

  const specificInstructions = categorySpecificInstructions[categoryName as keyof typeof categorySpecificInstructions] || 
    'Use their personal details, relationships, and life context to make the session feel uniquely tailored to them.';

  return `
BIOGRAPHICAL PERSONALIZATION (CRITICAL - MUST USE):
Biography: "${userBiography}"

INTEGRATION REQUIREMENTS:
- Weave specific details from their biography naturally throughout the session
- Reference their actual relationships, places, experiences, and personal context
- ${specificInstructions}
- Make the content feel like it was created specifically for this individual's life
- Use their real-world context to make abstract concepts more relatable and meaningful
- MANDATORY: Include at least 3-5 specific biographical references throughout the session`;
};

// Helper function for name instructions
const createNameInstructions = (nameResult: NamePersonalizationResult): string => {
  if (nameResult.displayName === 'there') {
    return 'PERSONALIZATION: Address user generically without using specific names.';
  }
  return `PERSONALIZATION: Address user as "${nameResult.displayName}" throughout the session for personal connection.`;
};

// Helper function to create group session personalization with deep individual integration
import { createMemberDistributionStrategy, createMentionRequirements } from './groupMemberDistribution';

const createGroupSessionPersonalization = (groupSession: GroupSession, categoryName: string, data: QuestionnaireData): string => {
  const { group_name, group_type, description, participants } = groupSession;
  
  // Create equitable member distribution strategy
  const distributionStrategy = createMemberDistributionStrategy(participants);
  const mentionRequirements = createMentionRequirements(distributionStrategy);
  
  // Create ultra-detailed participant profiles with deep psychological integration
  const participantProfiles = participants.map(p => {
    if (!p.consentToName) return 'a valued anonymous member bringing their unique energy to our circle';
    
    const name = p.preferredName || p.firstName;
    const pronoun = p.pronouns ? ` (${p.pronouns})` : '';
    
    // Build rich psychological and contextual profile
    const personalityContext = [];
    const professionalContext = [];
    const emotionalContext = [];
    const aspirationalContext = [];
    
    // Professional/Role Context
    if (p.subgroup) professionalContext.push(`from our ${p.subgroup} team`);
    if (p.roleTitle) professionalContext.push(`serving as ${p.roleTitle}`);
    if (p.locationTag) professionalContext.push(`connecting from ${p.locationTag}`);
    
    // Personal Interests & Lifestyle
    if (p.likes?.length) {
      const interests = p.likes.slice(0, 3);
      personalityContext.push(`who finds joy in ${interests.join(', ')}`);
    }
    
    // Emotional & Challenge Context
    if (p.personalChallenge) emotionalContext.push(`navigating ${p.personalChallenge}`);
    if (p.connectionStyle) emotionalContext.push(`with a ${p.connectionStyle} approach to connection`);
    if (p.backgroundStory) emotionalContext.push(`sharing: "${p.backgroundStory}"`);
    
    // Meditation & Growth Context
    if (p.meditationExperience) aspirationalContext.push(`bringing ${p.meditationExperience} meditation experience`);
    if (p.hopeForSession) aspirationalContext.push(`seeking "${p.hopeForSession}"`);
    
    const fullContext = [
      ...professionalContext,
      ...personalityContext,
      ...emotionalContext,
      ...aspirationalContext
    ].filter(Boolean);
    
    return `${name}${pronoun}${fullContext.length ? ' - ' + fullContext.join(', ') : ''}`;
  });

  // Deep collective analysis - extract nuanced group dynamics
  const collectiveHopes = participants
    .filter(p => p.hopeForSession && p.consentToName)
    .map(p => ({ name: p.preferredName || p.firstName, hope: p.hopeForSession }))
    .filter(item => item.hope);
  
  const sharedChallenges = participants
    .filter(p => p.personalChallenge && p.consentToName)
    .map(p => ({ name: p.preferredName || p.firstName, challenge: p.personalChallenge }))
    .filter(item => item.challenge);

  const experienceLevels = participants
    .filter(p => p.meditationExperience && p.consentToName)
    .map(p => ({ name: p.preferredName || p.firstName, level: p.meditationExperience }))
    .filter(item => item.level);

  // Connection style analysis for group dynamics
  const connectionStyles = participants
    .filter(p => p.connectionStyle && p.consentToName)
    .map(p => ({ name: p.preferredName || p.firstName, style: p.connectionStyle }))
    .filter(item => item.style);

  // Interest mapping for shared ground
  const interestMap = new Map();
  participants.forEach(p => {
    if (p.likes?.length && p.consentToName) {
      const name = p.preferredName || p.firstName;
      p.likes.forEach(interest => {
        if (!interestMap.has(interest)) interestMap.set(interest, []);
        interestMap.get(interest).push(name);
      });
    }
  });
  
  const sharedInterests = Array.from(interestMap.entries())
    .filter(([_, names]) => names.length > 1)
    .map(([interest, names]) => ({ interest, members: names }));

  // Location diversity analysis
  const locations = participants
    .filter(p => p.locationTag && p.consentToName)
    .map(p => ({ name: p.preferredName || p.firstName, location: p.locationTag }))
    .filter(item => item.location);

  // Professional subgroup analysis  
  const subgroups = new Map();
  participants.forEach(p => {
    if (p.subgroup && p.consentToName) {
      const name = p.preferredName || p.firstName;
      if (!subgroups.has(p.subgroup)) subgroups.set(p.subgroup, []);
      subgroups.get(p.subgroup).push(name);
    }
  });

  const teamDynamics = Array.from(subgroups.entries())
    .map(([team, members]) => ({ team, members }));

  const categorySpecificGroupInstructions = {
    meditation: 'Create a shared mindfulness experience that brings the group together in collective awareness and presence.',
    anxiety: 'Guide the group through collective calming techniques, emphasizing shared support and community strength.',
    sleep: 'Lead a group relaxation that helps everyone settle into peaceful rest together.',
    motivation: 'Build collective energy and group empowerment, celebrating shared goals and mutual support.',
    memory: 'Enhance group cognitive connection and shared memory experiences.',
    massage: 'Guide the group through collective body awareness and shared relaxation.',
    be_still: 'Create a sanctuary of shared stillness and emotional reset for the entire group.',
    self_love: 'Foster group self-compassion and collective healing through shared acceptance.'
  };

  const specificInstructions = categorySpecificGroupInstructions[categoryName as keyof typeof categorySpecificGroupInstructions] || 
    'Create content that addresses the group collectively, fostering connection and shared experience.';

  return `
GROUP SESSION CONTEXT (CRITICAL - MUST INTEGRATE):
- Group Leader: Michele Young (Minnesota-based therapist and yoga instructor)
- Group Name: "${group_name}"
- Group Type: ${group_type} group
- Description: ${description || 'A supportive community gathering'}
- Meeting Context: Sunday gatherings for breath, movement, and connection
- Community Focus: Women's circle emphasizing growth, healing, and community support
${data.specialOccasions ? `\n🎉 SPECIAL OCCASION TODAY (TOP PRIORITY - MUST BE CENTRAL THEME): ${data.specialOccasions}\n- Make this celebration the MAIN THEME throughout the entire session\n- Reference this special occasion frequently and weave celebration language throughout\n- Use this as the primary motivational anchor and source of inspiration` : ''}

PARTICIPANT PROFILES (ULTRA-DETAILED INDIVIDUAL CONTEXTS):
${participantProfiles.map(profile => `• ${profile}`).join('\n')}

DEEP COLLECTIVE ANALYSIS:
${collectiveHopes.length ? `- Individual Hopes Integration: ${collectiveHopes.map(h => `${h.name} seeks "${h.hope}"`).join('; ')}` : ''}
${sharedChallenges.length ? `- Personal Challenges Weaving: ${sharedChallenges.map(c => `${c.name} navigating ${c.challenge}`).join('; ')}` : ''}
${experienceLevels.length ? `- Experience Level Bridge: ${experienceLevels.map(e => `${e.name} brings ${e.level}`).join('; ')}` : ''}
${connectionStyles.length ? `- Connection Style Harmony: ${connectionStyles.map(s => `${s.name} prefers ${s.style}`).join('; ')}` : ''}
${sharedInterests.length ? `- Common Interest Ground: ${sharedInterests.map(i => `${i.interest} connects ${i.members.join(' & ')}`).join('; ')}` : ''}
${locations.length ? `- Geographic Unity: ${locations.map(l => `${l.name} from ${l.location}`).join('; ')}` : ''}
${teamDynamics.length ? `- Professional Dynamics: ${teamDynamics.map(t => `${t.team} team: ${t.members.join(', ')}`).join('; ')}` : ''}

ULTRA-DEEP GROUP INTEGRATION REQUIREMENTS:
- Reference Michele Young as the guiding therapist and yoga instructor
- Acknowledge the Sunday gathering tradition and community sanctuary
- ${specificInstructions}
- MANDATORY DEEP PERSONALIZATION: Weave individual member details throughout - mention their specific hopes, challenges, interests, and backgrounds to create meaningful personal connections within the group experience
- Bridge different experience levels by having those with more experience guide beginners naturally
- Create metaphors that draw from their diverse professional backgrounds and interests
- Address professional subgroups when relevant ("As our ${teamDynamics.length ? teamDynamics[0]?.team : 'design'} team settles in..." or "Marketing folks, let your creativity flow...")
- Reference location diversity to build unity across distances ("Whether you're joining from ${locations.length ? locations[0]?.location : 'Minneapolis'} or anywhere else...")
- Connect individual challenges to collective strength - show how each person's struggle contributes to group wisdom
- Weave personal hopes into shared intentions - combine individual aspirations into group vision
- Use provided pronouns respectfully and create inclusive language that honors each person's identity
- Create moments where different connection styles can shine (quiet reflection for introverts, group energy for extroverts)
- Reference shared interests to build authentic connections between members
- Honor each person's background story by weaving their context into group metaphors and guidance
- Use inclusive language that celebrates both individual uniqueness and collective experience
- Foster mutual support by highlighting how different members' strengths complement each other
- Create content that makes each person feel truly seen while building group cohesion
- MANDATORY: Include deeply personal touches that show you know each member's journey while building community
- Never use individual personal biography - focus entirely on group dynamics and the rich individual contexts provided above
- Transform individual contexts into collective wisdom and shared group strength
- Make frequent references to "our ${group_name} community" and build content that honors both personal journeys and group unity

${mentionRequirements}`;
};

export const createMeditationPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'meditation', data);
    return `🧘 COMPREHENSIVE GROUP MEDITATION SESSION 🧘

TARGET: Create a {{SESSION_LENGTH}} guided group meditation ({{TARGET_WORDS}} words) that provides deep, transformative mindfulness experience for the collective.

${groupSection}

MEDITATION SPECIFICATIONS:
- Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType}
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Specific Focus: ${data.specificFocus || 'Collective mindfulness and shared inner peace'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP SETTLING (2-3 minutes): Collective grounding and shared breath awareness
2. BREATH FOUNDATION (3-4 minutes): Synchronized breathing and group rhythm establishment
3. COLLECTIVE MINDFULNESS (5-7 minutes): Shared present moment awareness and group consciousness
4. DEEPER GROUP AWARENESS (2-3 minutes): Expanded collective consciousness and shared wisdom
5. INTEGRATION & GROUP CLOSING (1-2 minutes): Bringing awareness back together and setting group intentions

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group meditation experience

${data.additionalNotes ? `\nADDITIONAL GROUP FOCUS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'meditation');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'meditation');
  
  return `🧘 COMPREHENSIVE MEDITATION SESSION 🧘

TARGET: Create a {{SESSION_LENGTH}} guided meditation ({{TARGET_WORDS}} words) that provides deep, transformative mindfulness experience.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

MEDITATION SPECIFICATIONS:
- Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType}
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Specific Focus: ${data.specificFocus || 'General mindfulness and inner peace'}

CONTENT STRUCTURE (MANDATORY):
1. SETTLING IN (2-3 minutes): Gentle grounding and breath awareness
2. BREATH FOUNDATION (3-4 minutes): Deep breathing techniques and rhythm establishment
3. MINDFULNESS CORE (5-7 minutes): Present moment awareness and thought observation
4. DEEPER AWARENESS (2-3 minutes): Expanded consciousness and inner wisdom
5. INTEGRATION & CLOSING (1-2 minutes): Bringing awareness back and setting intentions

TARGET WORD COUNT: {{TARGET_WORDS}} words for full {{SESSION_LENGTH}} experience

${data.additionalNotes ? `\nADDITIONAL FOCUS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

export const createAnxietyReliefPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'anxiety', data);
    return `🌊 COMPREHENSIVE GROUP ANXIETY RELIEF SESSION 🌊

TARGET: Create a {{SESSION_LENGTH}} group anxiety relief session ({{TARGET_WORDS}} words) that provides immediate calm and long-term coping tools for the collective.

${groupSection}

ANXIETY RELIEF SPECIFICATIONS:
- Relief Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (extra gentle and reassuring)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Current Anxiety Trigger: ${data.specificFocus || 'Group anxiety and stress management'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP IMMEDIATE GROUNDING (2-3 minutes): Collective safety establishment and panic interruption
2. SHARED BREATH REGULATION (3-4 minutes): Group calming breath techniques and nervous system reset
3. COLLECTIVE TENSION RELEASE (3-4 minutes): Group progressive muscle relaxation and somatic relief
4. GROUP COGNITIVE REFRAMING (3-4 minutes): Shared thought pattern interruption and perspective shift
5. COLLECTIVE CONFIDENCE BUILDING (2-3 minutes): Group inner strength activation and future resilience

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group therapeutic experience

${data.additionalNotes ? `\nSPECIFIC GROUP ANXIETY CONTEXT: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'anxiety');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'anxiety');

  return `🌊 COMPREHENSIVE ANXIETY RELIEF SESSION 🌊

TARGET: Create a {{SESSION_LENGTH}} anxiety relief session ({{TARGET_WORDS}} words) that provides immediate calm and long-term coping tools.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

ANXIETY RELIEF SPECIFICATIONS:
- Relief Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (extra gentle and reassuring)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Current Anxiety Trigger: ${data.specificFocus || 'General anxiety and stress'}

CONTENT STRUCTURE (MANDATORY):
1. IMMEDIATE GROUNDING (2-3 minutes): Safety establishment and panic interruption
2. BREATH REGULATION (3-4 minutes): Calming breath techniques and nervous system reset
3. BODY TENSION RELEASE (3-4 minutes): Progressive muscle relaxation and somatic relief
4. COGNITIVE REFRAMING (3-4 minutes): Thought pattern interruption and perspective shift
5. CONFIDENCE BUILDING (2-3 minutes): Inner strength activation and future resilience

TARGET WORD COUNT: {{TARGET_WORDS}} words for full therapeutic experience

${data.additionalNotes ? `\nSPECIFIC ANXIETY CONTEXT: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

export const createSleepPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // Check if this is a bedtime story session
  const isBedtimeStory = data.sessionType === 'Bedtime Story Session' || data.groupSessionType === 'Group Bedtime Story Session';
  
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'sleep', data);
    
    if (isBedtimeStory) {
      return `📖 PERSONALIZED GROUP BEDTIME STORY SESSION 📖

TARGET: Create a {{SESSION_LENGTH}} soothing bedtime story ({{TARGET_WORDS}} words) designed to gently guide the group into peaceful sleep.

${groupSection}

BEDTIME STORY SPECIFICATIONS:
- Story Theme: ${data.groupStoryTheme || 'Family & Friendship stories'}
- Audience: ${data.groupStoryAudience || 'Mixed ages'}
- Voice Tone: ${data.voiceType || 'Gentle storyteller tone'}
- Session Length: ${data.sessionLength}
- Sleep Focus: ${data.specificFocus || 'Gentle transition to peaceful sleep through storytelling'}

STORY STRUCTURE (MANDATORY):
1. STORY OPENING & GROUP SETTLING (2-3 minutes): Welcome to the story world, gentle introduction of characters and setting
2. ADVENTURE BEGINNING (3-4 minutes): Engaging but calming story development, introduction of gentle conflict or journey
3. PEACEFUL RESOLUTION (3-4 minutes): Gradual slowing of story pace, characters finding comfort and safety
4. DREAMY CONCLUSION (2-3 minutes): Story winds down with characters settling into rest, connecting to listeners' own sleep
5. SLEEP TRANSITION (1-2 minutes): Final gentle guidance from story world to peaceful sleep

BEDTIME STORY REQUIREMENTS:
- Create age-appropriate content suitable for ${data.groupStoryAudience || 'mixed ages'}
- Use progressively slower pacing and softer language as story progresses
- Include gentle, sleep-inducing imagery and peaceful resolutions
- NO scary, exciting, or stimulating content that might prevent sleep
- Incorporate calming nature elements, cozy settings, and comfort themes
- Make characters relatable and comforting for the intended audience
- End with characters peacefully sleeping or resting, modeling sleep behavior
- Use group member details when appropriate to personalize the experience

TARGET WORD COUNT: {{TARGET_WORDS}} words for complete bedtime story journey

${data.additionalNotes ? `\nSPECIFIC GROUP STORY PREFERENCES: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
    }
    
    return `🌙 COMPREHENSIVE GROUP SLEEP PREPARATION SESSION 🌙

TARGET: Create a {{SESSION_LENGTH}} group sleep session ({{TARGET_WORDS}} words) that guides the collective from wakefulness to deep relaxation and sleep readiness.

${groupSection}

SLEEP SESSION SPECIFICATIONS:
- Sleep Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (increasingly soft and soothing)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Sleep Challenge: ${data.specificFocus || 'Group sleep preparation and relaxation'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP TRANSITION FROM DAY (2-3 minutes): Collective release of daily tensions and worries
2. SHARED BODY PREPARATION (3-4 minutes): Group progressive relaxation from head to toe
3. COLLECTIVE MIND QUIETING (3-4 minutes): Shared thought slowing and mental release techniques
4. GROUP DREAM PREPARATION (3-4 minutes): Collective positive visualization and sleep story elements
5. SHARED SLEEP INDUCTION (2-3 minutes): Final group relaxation and drift into sleep

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group sleep preparation journey

${data.additionalNotes ? `\nSPECIFIC GROUP SLEEP CONCERNS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'sleep');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'sleep');
  
  if (isBedtimeStory) {
    const biographyStorySection = userBiography ? createBedtimeStoryBiographySection(userBiography, data) : '';
    
    return `📖 PERSONALIZED BEDTIME STORY SESSION 📖

TARGET: Create a {{SESSION_LENGTH}} soothing bedtime story ({{TARGET_WORDS}} words) designed to gently guide into peaceful sleep.

${createNameInstructions(nameResult)}${biographyStorySection}${environmentSection}

BEDTIME STORY SPECIFICATIONS:
- Story Theme: ${data.storyTheme || 'Family & Friendship'}
- Audience: ${data.storyAudience || 'Adults'}
- Voice Tone: ${data.voiceType || 'Gentle storyteller tone'}
- Personal Details: ${data.includePersonalDetails || 'Keep it general'}
- Session Length: ${data.sessionLength}
- Sleep Focus: ${data.specificFocus || 'Gentle transition to peaceful sleep through storytelling'}

STORY STRUCTURE (MANDATORY):
1. STORY OPENING & SETTLING (2-3 minutes): Welcome to the story world, gentle introduction of characters and setting
2. ADVENTURE BEGINNING (3-4 minutes): Engaging but calming story development, introduction of gentle conflict or journey
3. PEACEFUL RESOLUTION (3-4 minutes): Gradual slowing of story pace, characters finding comfort and safety
4. DREAMY CONCLUSION (2-3 minutes): Story winds down with characters settling into rest, connecting to listener's own sleep
5. SLEEP TRANSITION (1-2 minutes): Final gentle guidance from story world to peaceful sleep

BEDTIME STORY REQUIREMENTS:
- Create age-appropriate content suitable for ${data.storyAudience || 'adults'}
- Use progressively slower pacing and softer language as story progresses
- Include gentle, sleep-inducing imagery and peaceful resolutions
- NO scary, exciting, or stimulating content that might prevent sleep
- Incorporate calming nature elements, cozy settings, and comfort themes
- Make characters relatable and comforting for the intended audience
- End with characters peacefully sleeping or resting, modeling sleep behavior
${data.includePersonalDetails && data.includePersonalDetails !== 'No - Keep it general' ? '- Incorporate personal details from biography naturally into the story world' : '- Keep story general without specific personal references'}

TARGET WORD COUNT: {{TARGET_WORDS}} words for complete bedtime story journey

${data.additionalNotes ? `\nSPECIFIC STORY PREFERENCES: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  return `🌙 COMPREHENSIVE SLEEP PREPARATION SESSION 🌙

TARGET: Create a {{SESSION_LENGTH}} sleep session ({{TARGET_WORDS}} words) that guides from wakefulness to deep relaxation and sleep readiness.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

SLEEP SESSION SPECIFICATIONS:
- Sleep Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (increasingly soft and soothing)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Sleep Challenge: ${data.specificFocus || 'General sleep preparation and relaxation'}

CONTENT STRUCTURE (MANDATORY):
1. TRANSITION FROM DAY (2-3 minutes): Releasing daily tensions and worries
2. BODY PREPARATION (3-4 minutes): Progressive relaxation from head to toe
3. MIND QUIETING (3-4 minutes): Thought slowing and mental release techniques
4. DREAM PREPARATION (3-4 minutes): Positive visualization and sleep story elements
5. SLEEP INDUCTION (2-3 minutes): Final relaxation and drift into sleep

TARGET WORD COUNT: {{TARGET_WORDS}} words for full sleep preparation journey

${data.additionalNotes ? `\nSPECIFIC SLEEP CONCERNS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

// Helper function to create bedtime story-specific biography integration
const createBedtimeStoryBiographySection = (userBiography: string, data: QuestionnaireData): string => {
  const includePersonal = data.includePersonalDetails && data.includePersonalDetails !== 'No - Keep it general';
  
  if (!includePersonal) {
    return '';
  }
  
  return `
BEDTIME STORY BIOGRAPHY INTEGRATION (CRITICAL - MUST USE):
Biography: "${userBiography}"

STORY PERSONALIZATION REQUIREMENTS:
- Extract pets, family members, and loved ones to become gentle story characters
- Use their favorite places, rooms, or locations as peaceful story settings
- Incorporate their hobbies and interests into calming story activities
- Reference their achievements and positive experiences as story elements
- Transform their personal spaces (bedroom, cozy spots) into story sanctuaries
- Use their relationships and bonds as sources of story comfort and love
- Make their real-world context the foundation for a soothing, sleep-inducing narrative
- MANDATORY: Weave at least 3-5 specific biographical elements naturally throughout the bedtime story
- Create characters inspired by their real people and pets, but in a gentle, story-appropriate way
- Use their actual interests and spaces to build a familiar, comforting story world that helps them drift into sleep`;
};

export const createMotivationPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'motivation', data);
    return `⚡ COMPREHENSIVE GROUP MOTIVATION & EMPOWERMENT SESSION ⚡

🎉 **MANDATORY CELEBRATION PRIORITY** (IF APPLICABLE):
${data.specialOccasions ? `TODAY'S CELEBRATION FOCUS: ${data.specialOccasions}

**CRITICAL INSTRUCTION**: This celebration MUST be the central theme throughout the ENTIRE session.
- Open with celebration energy
- Weave celebration references into every section
- Use this special occasion as the primary motivational anchor
- Close with celebration commitment and shared joy
- Treat this as the #1 priority above all other instructions

TARGET: Create a {{SESSION_LENGTH}} high-energy CELEBRATION-FOCUSED group motivation session ({{TARGET_WORDS}} words) that centers around this special occasion while building collective confidence, clarity, and unstoppable drive.` : `TARGET: Create a {{SESSION_LENGTH}} high-energy group motivation session ({{TARGET_WORDS}} words) that builds collective confidence, clarity, and unstoppable drive.`}

${groupSection}

MOTIVATION SESSION SPECIFICATIONS:
- Energy Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (confident and inspiring)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Motivation Focus: ${data.specificFocus || 'Collective success and group achievement motivation'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP ENERGY ACTIVATION (2-3 minutes): Collective confidence building and shared inner power awakening
2. SHARED VISION CLARITY (3-4 minutes): Group goal visualization and collective purpose connection
3. COLLECTIVE STRENGTH BUILDING (3-4 minutes): Shared victory reminders and group capability recognition
4. GROUP OBSTACLE OVERCOMING (3-4 minutes): Collective challenge reframing and solution focus
5. SHARED ACTION COMMITMENT (2-3 minutes): Group success visualization and mutual commitment sealing

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group motivational transformation

${data.additionalNotes ? `\nSPECIFIC GROUP MOTIVATION GOALS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'motivation');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'motivation');

  return `⚡ COMPREHENSIVE MOTIVATION & EMPOWERMENT SESSION ⚡

TARGET: Create a {{SESSION_LENGTH}} high-energy motivation session ({{TARGET_WORDS}} words) that builds confidence, clarity, and unstoppable drive.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

MOTIVATION SESSION SPECIFICATIONS:
- Energy Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (confident and inspiring)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Motivation Focus: ${data.specificFocus || 'General success and achievement motivation'}
${data.specialOccasions ? `\n🎉 TODAY'S CELEBRATION FOCUS: ${data.specialOccasions}\n- Make this celebration the PRIMARY motivational theme\n- Reference this special occasion throughout as the main source of inspiration and energy` : ''}

CONTENT STRUCTURE (MANDATORY):
1. ENERGY ACTIVATION (2-3 minutes): Confidence building and inner power awakening
2. VISION CLARITY (3-4 minutes): Goal visualization and purpose connection
3. STRENGTH BUILDING (3-4 minutes): Past victory reminders and capability recognition
4. OBSTACLE OVERCOMING (3-4 minutes): Challenge reframing and solution focus
5. ACTION COMMITMENT (2-3 minutes): Future success visualization and commitment sealing

TARGET WORD COUNT: {{TARGET_WORDS}} words for full motivational transformation

${data.additionalNotes ? `\nSPECIFIC MOTIVATION GOALS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

export const createMemoryPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'memory', data);
    return `🧠 COMPREHENSIVE GROUP MEMORY ENHANCEMENT SESSION 🧠

TARGET: Create a {{SESSION_LENGTH}} group memory enhancement session ({{TARGET_WORDS}} words) that builds collective cognitive strength, recall ability, and mental clarity.

${groupSection}

MEMORY SESSION SPECIFICATIONS:
- Enhancement Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (clear and instructive)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Memory Focus: ${data.specificFocus || 'Group memory improvement and collective mental clarity'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP MENTAL PREPARATION (2-3 minutes): Collective focus establishment and cognitive readiness
2. SHARED MEMORY ACTIVATION (3-4 minutes): Group recall exercises and cognitive warm-up
3. COLLECTIVE ENHANCEMENT TECHNIQUES (3-4 minutes): Shared memory palace and association methods
4. GROUP RETENTION PRACTICE (3-4 minutes): Collective information encoding and storage techniques
5. SHARED INTEGRATION & RECALL (2-3 minutes): Group memory consolidation and future application

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group cognitive enhancement

${data.additionalNotes ? `\nSPECIFIC GROUP MEMORY GOALS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'memory');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'memory');

  return `🧠 COMPREHENSIVE MEMORY ENHANCEMENT SESSION 🧠

TARGET: Create a {{SESSION_LENGTH}} memory enhancement session ({{TARGET_WORDS}} words) that builds cognitive strength, recall ability, and mental clarity.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

MEMORY SESSION SPECIFICATIONS:
- Enhancement Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (clear and instructive)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Memory Focus: ${data.specificFocus || 'General memory improvement and mental clarity'}

CONTENT STRUCTURE (MANDATORY):
1. MENTAL PREPARATION (2-3 minutes): Focus establishment and cognitive readiness
2. MEMORY ACTIVATION (3-4 minutes): Recall exercises and cognitive warm-up
3. ENHANCEMENT TECHNIQUES (3-4 minutes): Memory palace and association methods
4. RETENTION PRACTICE (3-4 minutes): Information encoding and storage techniques
5. INTEGRATION & RECALL (2-3 minutes): Memory consolidation and future application

TARGET WORD COUNT: {{TARGET_WORDS}} words for full cognitive enhancement

${data.additionalNotes ? `\nSPECIFIC MEMORY GOALS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

export const createMassagePrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'massage', data);
    return `🤲 COMPREHENSIVE GROUP BODY RELAXATION SESSION 🤲

TARGET: Create a {{SESSION_LENGTH}} group body relaxation session ({{TARGET_WORDS}} words) that provides deep muscle relief, tension release, and physical restoration for the collective.

${groupSection}

MASSAGE SESSION SPECIFICATIONS:
- Relaxation Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (soothing and gentle)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Body Focus: ${data.specificFocus || 'Group body tension relief and collective muscle relaxation'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP BODY AWARENESS (2-3 minutes): Collective physical grounding and shared tension identification
2. SHARED MUSCLE PREPARATION (3-4 minutes): Group gentle warming and circulation activation
3. COLLECTIVE TENSION RELEASE (3-4 minutes): Shared progressive muscle relaxation and pressure point work
4. GROUP DEEP RESTORATION (3-4 minutes): Collective full body integration and healing flow
5. SHARED INTEGRATION & COMFORT (2-3 minutes): Group complete relaxation and body appreciation

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group physical restoration

${data.additionalNotes ? `\nSPECIFIC GROUP BODY CONCERNS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'massage');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'massage');

  return `🤲 COMPREHENSIVE BODY RELAXATION SESSION 🤲

TARGET: Create a {{SESSION_LENGTH}} body relaxation session ({{TARGET_WORDS}} words) that provides deep muscle relief, tension release, and physical restoration.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

MASSAGE SESSION SPECIFICATIONS:
- Relaxation Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (soothing and gentle)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Body Focus: ${data.specificFocus || 'General body tension relief and muscle relaxation'}

CONTENT STRUCTURE (MANDATORY):
1. BODY AWARENESS (2-3 minutes): Physical grounding and tension identification
2. MUSCLE PREPARATION (3-4 minutes): Gentle warming and circulation activation
3. TENSION RELEASE (3-4 minutes): Progressive muscle relaxation and pressure point work
4. DEEP RESTORATION (3-4 minutes): Full body integration and healing flow
5. INTEGRATION & COMFORT (2-3 minutes): Complete relaxation and body appreciation

TARGET WORD COUNT: {{TARGET_WORDS}} words for full physical restoration

${data.additionalNotes ? `\nSPECIFIC BODY CONCERNS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

export const createBeStillPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'be_still', data);
    return `🕯️ COMPREHENSIVE GROUP EMOTIONAL RESET SESSION 🕯️

TARGET: Create a {{SESSION_LENGTH}} group emotional reset session ({{TARGET_WORDS}} words) that provides deep stillness, emotional balance, and inner peace restoration for the collective.

${groupSection}

EMOTIONAL RESET SPECIFICATIONS:
- Reset Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (gentle and nurturing)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Emotional Focus: ${data.specificFocus || 'Group emotional overwhelm and collective inner peace restoration'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP EMOTIONAL GROUNDING (2-3 minutes): Collective safety establishment and shared emotional acknowledgment
2. SHARED STILLNESS PREPARATION (3-4 minutes): Group mental quieting and collective inner space creation
3. COLLECTIVE DEEP RESET (3-4 minutes): Group emotional release and shared center restoration
4. GROUP BALANCE RESTORATION (3-4 minutes): Collective inner harmony and stability building
5. SHARED RENEWED STRENGTH (2-3 minutes): Group emotional resilience and collective forward momentum

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group emotional restoration

${data.additionalNotes ? `\nSPECIFIC GROUP EMOTIONAL NEEDS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'be_still');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'be_still');

  return `🕯️ COMPREHENSIVE EMOTIONAL RESET SESSION 🕯️

TARGET: Create a {{SESSION_LENGTH}} emotional reset session ({{TARGET_WORDS}} words) that provides deep stillness, emotional balance, and inner peace restoration.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

EMOTIONAL RESET SPECIFICATIONS:
- Reset Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (gentle and nurturing)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Emotional Focus: ${data.specificFocus || 'General emotional overwhelm and inner peace restoration'}

CONTENT STRUCTURE (MANDATORY):
1. EMOTIONAL GROUNDING (2-3 minutes): Safety establishment and emotional acknowledgment
2. STILLNESS PREPARATION (3-4 minutes): Mental quieting and inner space creation
3. DEEP RESET (3-4 minutes): Emotional release and center restoration
4. BALANCE RESTORATION (3-4 minutes): Inner harmony and stability building
5. RENEWED STRENGTH (2-3 minutes): Emotional resilience and forward momentum

TARGET WORD COUNT: {{TARGET_WORDS}} words for full emotional restoration

${data.additionalNotes ? `\nSPECIFIC EMOTIONAL NEEDS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

export const createSelfLovePrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For group sessions, use group context instead of individual personalization
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'self_love', data);
    return `💖 COMPREHENSIVE GROUP SELF-LOVE & COMPASSION SESSION 💖

TARGET: Create a {{SESSION_LENGTH}} group self-love session ({{TARGET_WORDS}} words) that builds collective inner kindness, self-acceptance, and emotional healing.

${groupSection}

SELF-LOVE SESSION SPECIFICATIONS:
- Love Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (warm and nurturing)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Self-Love Focus: ${data.specificFocus || 'Group self-acceptance and collective inner kindness cultivation'}

CONTENT STRUCTURE (MANDATORY):
1. GROUP SELF-ACKNOWLEDGMENT (2-3 minutes): Collective present moment self-recognition and acceptance
2. SHARED COMPASSION BUILDING (3-4 minutes): Group inner critic healing and kindness cultivation
3. COLLECTIVE LOVE ACTIVATION (3-4 minutes): Shared heart opening and group self-appreciation practices
4. GROUP HEALING INTEGRATION (3-4 minutes): Collective emotional wounds healing and forgiveness work
5. SHARED LOVE COMMITMENT (2-3 minutes): Group self-love vows and collective ongoing kindness promises

TARGET WORD COUNT: {{TARGET_WORDS}} words for full group self-love transformation

${data.additionalNotes ? `\nSPECIFIC GROUP SELF-LOVE NEEDS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

  // Individual session logic
  const nameResult = getPersonalizationName(user, data, profileData);
  const biographySection = createBiographicalPersonalization(userBiography, 'self_love');
  const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'self_love');

  return `💖 COMPREHENSIVE SELF-LOVE & COMPASSION SESSION 💖

TARGET: Create a {{SESSION_LENGTH}} self-love session ({{TARGET_WORDS}} words) that builds inner kindness, self-acceptance, and emotional healing.

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

SELF-LOVE SESSION SPECIFICATIONS:
- Love Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType} (warm and nurturing)
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Self-Love Focus: ${data.specificFocus || 'General self-acceptance and inner kindness cultivation'}

CONTENT STRUCTURE (MANDATORY):
1. SELF-ACKNOWLEDGMENT (2-3 minutes): Present moment self-recognition and acceptance
2. COMPASSION BUILDING (3-4 minutes): Inner critic healing and kindness cultivation
3. LOVE ACTIVATION (3-4 minutes): Heart opening and self-appreciation practices
4. HEALING INTEGRATION (3-4 minutes): Emotional wounds healing and forgiveness work
5. LOVE COMMITMENT (2-3 minutes): Self-love vows and ongoing kindness promises

TARGET WORD COUNT: {{TARGET_WORDS}} words for full self-love transformation

${data.additionalNotes ? `\nSPECIFIC SELF-LOVE NEEDS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
};

const createZenAfPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  if (groupSession) {
    const groupSection = createGroupSessionPersonalization(groupSession, 'zen af', data);
    return `🌟 ZEN AF GROUP SESSION 🌟

TARGET: Create a {{SESSION_LENGTH}} zen af session ({{TARGET_WORDS}} words).

CRITICAL ZEN AF LANGUAGE RULES:
- Maximum 6-8 F-bombs per minute of content for sustained raw energy
- Use explicit language therapeutically - NEVER to tear down, ALWAYS to build up and empower
- Allow 3-4 sentences of raw validation before therapeutic pivot (not immediate pivot)
- Tone: Irreverent, sarcastic, relatable, raw, but ultimately healing and empowering
- 18+ content - acknowledge frustration with modern life humor and pop culture references
- Include sarcastic comparisons to emails, notifications, toxic people, group chats, etc.
- Examples: "That circus outside - the emails, the notifications, the people who can't text back in complete sentences - let it all drift the fuck away" 
- Use empowering dismissals: "Not today, Karen" / "Bitch, be gone" / "Fuck that" 
- Create sustained raw sections before healing: "Tell every thought trying to drag you back: 'Bitch, be gone.' Breathe in power. Breathe out everyone else's panic."

${groupSection}

PERSONALIZATION FROM QUESTIONNAIRE:
- What's bothering you: ${data.specificFocus || 'General stress and frustration'}
- Why it's affecting you: ${data.triggers || 'Life pressures'}
- Communication style: ${data.preferredStyle || 'Raw and unfiltered'}
- Voice tone needed: ${data.voiceType || 'Calm and grounding'}
- Primary goal: ${data.primaryGoal || 'Release this frustration'}
- Session length: ${data.sessionLength || '10 minutes'}
- Current environment: ${data.environment || 'Personal space'}

INTEGRATION INSTRUCTIONS:
- Use their specific frustrations for targeted sarcastic commentary
- Create personalized metaphors based on their triggers (work = "office drama", relationships = "emotional vampires", etc.)
- Reference their actual environment in dismissive language ("Leave that [workplace] bullshit at your [desk/cubicle]")
- Match their preferred communication style with appropriate rawness level
- Build sustained raw validation around their specific issues before therapeutic pivot

TARGET WORD COUNT: {{TARGET_WORDS}} words

${data.additionalNotes ? `\nADDITIONAL ZEN AF FOCUS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  } else {
    const nameResult = getPersonalizationName(user, data, profileData);
    const biographySection = createBiographicalPersonalization(userBiography, 'zen af');
    const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, 'zen af');
    
    // Extract ALL INDIVIDUAL questionnaire data for comprehensive personalization
    const currentFeeling = (data as any).currentFeeling || data.currentState || 'overwhelmed by life\'s bullshit';
    const copingStyle = (data as any).copingStyle || 'direct confrontation of problems';
    const intensityLevel = (data as any).intensityLevel || 'high intensity validation';
    const stressors = data.stressors || 'work, relationships, daily frustrations';
    
    return `🌟 ZEN AF SESSION 🌟

TARGET: Create a {{SESSION_LENGTH}} zen af session ({{TARGET_WORDS}} words).

CRITICAL ZEN AF LANGUAGE RULES:
- Maximum 6-8 F-bombs per minute of content for sustained raw energy
- Use explicit language therapeutically - NEVER to tear down, ALWAYS to build up and empower
- Allow 3-4 sentences of raw validation before therapeutic pivot (not immediate pivot)
- Tone: Irreverent, sarcastic, relatable, raw, but ultimately healing and empowering
- 18+ content - acknowledge frustration with modern life humor and pop culture references
- Include sarcastic comparisons to emails, notifications, toxic people, group chats, etc.
- Examples: "That circus outside - the emails, the notifications, the people who can't text back in complete sentences - let it all drift the fuck away" 
- Use empowering dismissals: "Not today, Karen" / "Bitch, be gone" / "Fuck that" 
- Create sustained raw sections before healing: "Tell every thought trying to drag you back: 'Bitch, be gone.' Breathe in power. Breathe out everyone else's panic."

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

COMPREHENSIVE PERSONALIZATION FROM QUESTIONNAIRE (USE ALL DATA):
- What's bothering you: ${data.specificFocus || 'General stress and frustration'}
- Why it's affecting you: ${data.triggers || 'Life pressures'}
- Current emotional state: ${currentFeeling}
- Personal stressors: ${stressors}
- Communication style: ${data.preferredStyle || 'Raw and unfiltered'}
- Coping approach: ${copingStyle}
- Intensity level needed: ${intensityLevel}
- Voice tone needed: ${data.voiceType || 'Calm and grounding'}
- Primary goal: ${data.primaryGoal || 'Release this frustration'}
- Desired feeling: ${data.desiredFeeling || 'Empowered and stress-free'}
- Session length: ${data.sessionLength || '10 minutes'}
- Current environment: ${data.environment || 'Personal space'}
- Environment integration: ${data.useEnvironment ? 'Use current environment' : 'Generic setting'}
- Background sounds: ${data.backgroundSounds ? 'Include' : 'None'}

ENHANCED INTEGRATION INSTRUCTIONS:
- Address their current state: "${currentFeeling}" with raw therapeutic validation
- Target their specific stressors: "${stressors}" with personalized sarcastic commentary
- Use their triggers: "${data.triggers}" for targeted therapeutic dismissals
- Match their coping style: "${copingStyle}" with appropriate rawness approach
- Build toward their desired feeling: "${data.desiredFeeling}" through "${intensityLevel}" validation
- Create personalized metaphors based on their actual life situation and environment
- Reference their environment in dismissive language when appropriate
- Build sustained raw validation around their specific issues before therapeutic pivot
- Use their preferred communication style to calibrate rawness level appropriately

TARGET WORD COUNT: {{TARGET_WORDS}} words

${data.additionalNotes ? `\nADDITIONAL ZEN AF FOCUS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
  }

};

export const createStandardCategoryPrompt = (
  categoryName: string,
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  console.log('📝 Creating standard category prompt for:', categoryName);
  console.log('📝 COMPREHENSIVE INPUT VALIDATION:', {
    hasEnvironment: !!data.environment,
    environmentDetail: data.environment,
    hasGoal: !!data.primaryGoal,
    goalDetail: data.primaryGoal,
    hasCurrentState: !!data.currentState,
    stateDetail: data.currentState,
    hasSpecificFocus: !!data.specificFocus,
    focusDetail: data.specificFocus,
    hasDesiredFeeling: !!data.desiredFeeling,
    feelingDetail: data.desiredFeeling,
    useEnvironment: data.useEnvironment,
    sessionLength: data.sessionLength,
    voiceType: data.voiceType,
    preferredStyle: data.preferredStyle
  });

  // Enhanced environment context
  const environmentContext = data.environment 
    ? createEnvironmentContext(data.environment, categoryName)
    : 'No specific environment provided - use supportive, general language about their space.';
    
  console.log('🌍 Environment context length:', environmentContext.length);
  
  // Validate critical personalization elements
  const criticalElements = {
    environment: data.environment,
    goal: data.primaryGoal,
    currentState: data.currentState,
    specificFocus: data.specificFocus,
    desiredFeeling: data.desiredFeeling
  };
  
  const missingElements = Object.entries(criticalElements)
    .filter(([key, value]) => !value)
    .map(([key]) => key);
    
  if (missingElements.length > 0) {
    console.warn('⚠️ Missing personalization elements:', missingElements);
  } else {
    console.log('✅ All critical personalization elements present');
  }
  console.log('📝 Creating standard category prompt for:', categoryName);
  console.log('📝 Input validation:', {
    hasEnvironment: !!data.environment,
    environmentDetail: data.environment,
    hasGoal: !!data.primaryGoal,
    goalDetail: data.primaryGoal,
    hasCurrentState: !!data.currentState,
    stateDetail: data.currentState,
    hasSpecificFocus: !!data.specificFocus,
    focusDetail: data.specificFocus,
    hasDesiredFeeling: !!data.desiredFeeling,
    feelingDetail: data.desiredFeeling
  });
  switch (categoryName.toLowerCase()) {
    case 'meditation':
      return createMeditationPrompt(data, userBiography, profileData, user, groupSession);
    case 'anxiety':
      return createAnxietyReliefPrompt(data, userBiography, profileData, user, groupSession);
    case 'sleep':
      return createSleepPrompt(data, userBiography, profileData, user, groupSession);
    case 'motivation':
      return createMotivationPrompt(data, userBiography, profileData, user, groupSession);
    case 'memory':
      return createMemoryPrompt(data, userBiography, profileData, user, groupSession);
    case 'massage':
      return createMassagePrompt(data, userBiography, profileData, user, groupSession);
    case 'be_still':
      return createBeStillPrompt(data, userBiography, profileData, user, groupSession);
    case 'self_love':
      return createSelfLovePrompt(data, userBiography, profileData, user, groupSession);
    case 'zen af':
    case 'zen-af':
      return createZenAfPrompt(data, userBiography, profileData, user, groupSession);
    default:
      // Generic fallback - handle both group and individual sessions
      if (groupSession) {
        const groupSection = createGroupSessionPersonalization(groupSession, categoryName, data);
        return `🌟 ${categoryName.toUpperCase()} GROUP SESSION 🌟

TARGET: Create a {{SESSION_LENGTH}} group ${categoryName.toLowerCase()} session ({{TARGET_WORDS}} words).

${groupSection}

SESSION SPECIFICATIONS:
- Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType}
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Focus: ${data.specificFocus || `Collective ${categoryName.toLowerCase()} experience`}

TARGET WORD COUNT: {{TARGET_WORDS}} words

${data.additionalNotes ? `\nADDITIONAL GROUP FOCUS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
      } else {
        const nameResult = getPersonalizationName(user, data, profileData);
        const biographySection = createBiographicalPersonalization(userBiography, categoryName);
        const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, categoryName);
        return `🌟 ${categoryName.toUpperCase()} SESSION 🌟

TARGET: Create a {{SESSION_LENGTH}} ${categoryName.toLowerCase()} session ({{TARGET_WORDS}} words).

${createNameInstructions(nameResult)}${biographySection}${environmentSection}

SESSION SPECIFICATIONS:
- Style: ${data.preferredStyle}
- Voice Tone: ${data.voiceType}
- Primary Goal: ${data.primaryGoal}
- Session Length: ${data.sessionLength}
- Focus: ${data.specificFocus || `General ${categoryName.toLowerCase()} experience`}

TARGET WORD COUNT: {{TARGET_WORDS}} words

${data.additionalNotes ? `\nADDITIONAL FOCUS: ${data.additionalNotes}` : ''}${data.additionalInfo ? `\nIMPORTANT ADDITIONAL CONTEXT: ${data.additionalInfo}` : ''}`;
      }
  }
};